/**
 * Header Component
 * Handles mobile menu toggle and logo dark section detection
 */

(function () {
  'use strict';

  /**
   * Mobile Menu Toggle Handler
   */
  function initMobileMenuToggle() {
    const menuToggle = document.getElementById('mobile-menu');
    const mobileMenu = document.getElementById('mobile-nav-drawer');
    const sectionHeader = document.querySelector('.section-header');

    if (!menuToggle || !mobileMenu) return;

    const menuIcon = menuToggle.querySelector('.menu-icon');
    const closeIcon = menuToggle.querySelector('.close-icon');
    const siteOverlay = document.getElementById('site-overlay');
    const searchDrawer = document.getElementById('header-search-layer');

    /**
     * Close mobile menu
     */
    function closeMobileMenu() {
      const menuIcon = menuToggle.querySelector('.menu-icon');
      const closeIcon = menuToggle.querySelector('.close-icon');
      const siteOverlay = document.getElementById('site-overlay');

      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.classList.remove('menu-open', 'scroll-fixed');
      document.documentElement.classList.remove('scroll-fixed');
      document.body.style.top = '';
      document.body.style.position = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }

      mobileMenu.classList.remove('open', 'opacity-100', 'visible', 'z-2');
      mobileMenu.classList.add('opacity-0', 'invisible');
      mobileMenu.setAttribute('aria-hidden', 'true');
      mobileMenu.setAttribute('inert', '');
      menuToggle.setAttribute('aria-expanded', 'false');
      if (sectionHeader) sectionHeader.classList.remove('mobile-menu-active');
      if (menuIcon) {
        menuIcon.classList.remove('d-none');
        menuIcon.classList.add('d-block');
      }
      if (closeIcon) {
        closeIcon.classList.remove('d-block');
        closeIcon.classList.add('d-none');
      }
      if (siteOverlay) {
        siteOverlay.classList.remove('z-2', 'overlay--body');
      }
      SiteOverlay.hideOverlay();
    }

    /**
     * Open mobile menu
     */
    function openMobileMenu() {
      // Close search drawer if open before opening mobile menu
      // Do this manually to keep overlay visible
      if (searchDrawer && searchDrawer.classList.contains('open')) {
        searchDrawer.classList.remove('open');
        searchDrawer.isOpen = false;
        searchDrawer.setAttribute('inert', '');
        searchDrawer.setAttribute('aria-hidden', 'true');
        const searchToggle = searchDrawer.openedBy;
        if (searchToggle) {
          searchToggle.setAttribute('aria-expanded', 'false');
        }
        // Remove z-2 from overlay if search drawer had it, but keep overlay visible
        if (siteOverlay && searchDrawer.id === 'header-search-layer') {
          siteOverlay.classList.remove('z-2');
        }
      }

      // Ensure overlay is visible and has z-2 class for mobile menu
      if (siteOverlay) {
        // Show overlay (will return false if already visible, which is fine)
        SiteOverlay.showOverlay();
        // Ensure overlay--body class is present
        if (!siteOverlay.classList.contains('overlay--body')) {
          siteOverlay.classList.add('overlay--body');
        }
        // Add z-2 class for mobile menu
        siteOverlay.classList.add('z-2');
      } else {
        SiteOverlay.showOverlay();
      }

      // Prevent body scrolling when mobile menu is open
      // Store current scroll position to restore later
      const scrollY = window.scrollY;
      
      // Add classes to prevent scrolling
      document.body.classList.add('menu-open', 'scroll-fixed');
      document.documentElement.classList.add('scroll-fixed');
      document.body.style.top = `-${scrollY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      mobileMenu.classList.remove('opacity-0', 'invisible');
      mobileMenu.classList.add('open', 'opacity-100', 'visible', 'z-2');
      mobileMenu.removeAttribute('inert');
      mobileMenu.setAttribute('aria-hidden', 'false');
      menuToggle.setAttribute('aria-expanded', 'true');
      if (sectionHeader) sectionHeader.classList.add('mobile-menu-active');
      if (menuIcon) {
        menuIcon.classList.remove('d-block');
        menuIcon.classList.add('d-none');
      }
      if (closeIcon) {
        closeIcon.classList.remove('d-none');
        closeIcon.classList.add('d-block');
      }
    }

    // Toggle mobile menu on button click
    menuToggle.addEventListener('click', function (e) {
      e.preventDefault();
      const isOpen = mobileMenu.classList.contains('open');

      if (isOpen) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    });

    // Close menu on escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && mobileMenu.classList.contains('open')) {
        closeMobileMenu();
      }
    });

    // Close menu when clicking on overlay or outside menu
    document.addEventListener('click', function (e) {
      if (!mobileMenu.classList.contains('open')) return;

      // Check if click is outside the menu and not on the toggle button
      const isClickInsideMenu = mobileMenu.contains(e.target);
      const isClickOnToggle = menuToggle.contains(e.target);

      if (!isClickInsideMenu && !isClickOnToggle) {
        closeMobileMenu();
      }
    });
  }

  /**
   * Logo Dark Section Detection
   * Detects when logo overlaps dark sections and applies light-logo class
   */
  function initLogoDarkDetection() {
    const logoElements = document.querySelectorAll('[data-logo-element]');
    if (logoElements.length === 0) {
      return;
    }

    const darkClasses = ['is-dark', 'bg-dark', 'dark-section'];
    const selectors = darkClasses.map(function (cls) {
      return '.shopify-section .' + cls + ', .shopify-section.' + cls;
    }).join(', ');

    const darkSections = document.querySelectorAll(selectors);

    if (darkSections.length === 0) {
      return;
    }

    const logoStates = new Map();
    const debounceTimers = new Map();

    function checkLogoOverlap() {
      logoElements.forEach(function (logoElement) {
        const logoRect = logoElement.getBoundingClientRect();
        const logoCenter = logoRect.top + logoRect.height / 2;

        let isOverDark = false;

        darkSections.forEach(function (section) {
          const sectionRect = section.getBoundingClientRect();

          const buffer = 10;
          if (
            logoCenter >= (sectionRect.top - buffer) &&
            logoCenter <= (sectionRect.bottom + buffer)
          ) {
            isOverDark = true;
          }
        });

        const lastState = logoStates.get(logoElement);
        if (lastState !== isOverDark) {
          logoStates.set(logoElement, isOverDark);

          const existingTimer = debounceTimers.get(logoElement);
          if (existingTimer) {
            clearTimeout(existingTimer);
          }

          const timer = setTimeout(function () {
            if (isOverDark) {
              if (!logoElement.classList.contains('light-logo')) {
                logoElement.classList.add('light-logo');
              }
            } else {
              if (logoElement.classList.contains('light-logo')) {
                logoElement.classList.remove('light-logo');
              }
            }
          }, 50);

          debounceTimers.set(logoElement, timer);
        }
      });
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 0.9, 1]
    };

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function () {
        checkLogoOverlap();
      });
    }, observerOptions);

    darkSections.forEach(function (section) {
      observer.observe(section);
    });

    let scrollTimeout;
    window.addEventListener('scroll', function () {
      if (scrollTimeout) {
        window.cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = window.requestAnimationFrame(checkLogoOverlap);
    }, { passive: true });

    checkLogoOverlap();

    let resizeTimeout;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkLogoOverlap, 100);
    }, { passive: true });
  }

  /**
   * Initialize header functionality
   */
  function init() {
    initMobileMenuToggle();
    initLogoDarkDetection();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
